import type {
  HabitTag,
  MealItem,
  MealType,
  NormalizedDailyMetrics,
  WorkoutSession,
} from '@vitalai/health-models';
import { addDays, lastNDateKeys, parseDateKey, toDateKey } from '@vitalai/shared';
import { clamp, createSeededRandom, randomInRange, round } from '@vitalai/shared';

/**
 * DemoDataService: la unica fuente "demo" del sistema. Genera datos ya en
 * forma de NormalizedDailyMetrics, exactamente como lo haria un normalizador
 * real de HealthKit o Huawei Health, para que el resto de VitalAI (scoring,
 * baseline, trends, UI) nunca sepa que esta viendo datos simulados.
 */

interface DemoProfile {
  hrvBase: number;
  restingHrBase: number;
  sleepDurationBase: number;
  sleepEfficiencyBase: number;
  stepsBase: number;
  activeCaloriesBase: number;
  proteinBase: number;
  caloriesBase: number;
  weightBase: number;
}

const DEFAULT_PROFILE: DemoProfile = {
  hrvBase: 46,
  restingHrBase: 57,
  sleepDurationBase: 440,
  sleepEfficiencyBase: 88,
  stepsBase: 8200,
  activeCaloriesBase: 420,
  proteinBase: 135,
  caloriesBase: 2250,
  weightBase: 78,
};

export interface DemoDataOptions {
  /** Numero de dias de historico a generar. Por defecto 90. */
  days?: number;
  /** Ultimo dia del historico (YYYY-MM-DD). Por defecto, hoy en UTC. */
  endDate?: string;
  /** Semilla del generador pseudoaleatorio, para reproducibilidad. */
  seed?: number;
  profile?: Partial<DemoProfile>;
}

const MEAL_LIBRARY: Record<MealType, string[]> = {
  breakfast: ['Avena con frutas', 'Huevos revueltos con palta', 'Yogur griego con granola'],
  lunch: ['Pollo a la plancha con arroz', 'Ensalada de atun', 'Lomo saltado con vegetales'],
  dinner: ['Salmon al horno con vegetales', 'Sopa de lentejas', 'Pasta integral con pollo'],
  snack: ['Batido de proteina', 'Fruta y frutos secos', 'Barra de proteina'],
};

const MEAL_HOUR: Record<MealType, number> = {
  breakfast: 8,
  lunch: 13,
  dinner: 20,
  snack: 17,
};

function pickDailyTags(random: () => number): HabitTag[] {
  const tags: HabitTag[] = [];
  const roll = (probability: number) => random() < probability;

  if (roll(0.03)) {
    tags.push('sick');
    return tags;
  }

  if (roll(0.25)) tags.push('gym');
  else if (roll(0.18)) tags.push('cardio');
  else if (roll(0.12)) tags.push('rest_day');

  if (roll(0.1)) tags.push('sauna');
  if (roll(0.12)) tags.push('alcohol');
  if (roll(0.12)) tags.push('coffee_late');
  if (roll(0.15)) tags.push('heavy_dinner');
  if (roll(0.12)) tags.push('stress');
  if (roll(0.04)) tags.push('travel');
  if (roll(0.04)) tags.push('nasal_congestion');
  if (roll(0.1)) tags.push('nap');
  if (roll(0.1)) tags.push('high_workload');

  return tags;
}

function distributeShares(random: () => number, count: number): number[] {
  const raw = Array.from({ length: count }, () => 0.6 + random());
  const sum = raw.reduce((total, value) => total + value, 0);
  return raw.map((value) => value / sum);
}

function mealTimeFor(mealType: MealType, dateKey: string): string {
  const date = parseDateKey(dateKey);
  date.setUTCHours(MEAL_HOUR[mealType], 0, 0, 0);
  return date.toISOString();
}

function buildDemoMeals(
  random: () => number,
  dateKey: string,
  totals: { calories: number; protein: number; carbs: number; fat: number; fiber: number; sodium: number; sugar: number },
): MealItem[] {
  const mealTypes: MealType[] = ['breakfast', 'lunch', 'dinner'];
  if (random() < 0.6) mealTypes.push('snack');
  const shares = distributeShares(random, mealTypes.length);

  return mealTypes.map((mealType, index) => {
    const share = shares[index] ?? 1 / mealTypes.length;
    const options = MEAL_LIBRARY[mealType];
    const name = options[Math.floor(random() * options.length)] ?? options[0]!;
    return {
      id: `${dateKey}-${mealType}`,
      name,
      mealType,
      time: mealTimeFor(mealType, dateKey),
      calories: round(totals.calories * share),
      protein: round(totals.protein * share),
      carbs: round(totals.carbs * share),
      fat: round(totals.fat * share),
      fiber: round(totals.fiber * share),
      sodium: round(totals.sodium * share),
      sugar: round(totals.sugar * share),
    };
  });
}

/** Genera `days` dias de datos normalizados con variabilidad realista, terminando en `endDate`. */
export function generateDemoHistory(options: DemoDataOptions = {}): NormalizedDailyMetrics[] {
  const days = options.days ?? 90;
  const endDateKey = options.endDate ?? toDateKey(new Date());
  const profile: DemoProfile = { ...DEFAULT_PROFILE, ...options.profile };
  const random = createSeededRandom(options.seed ?? 20260101);
  const dateKeys = lastNDateKeys(endDateKey, days);

  const history: NormalizedDailyMetrics[] = [];
  let previousDayTags: HabitTag[] = [];
  let weightDrift = 0;

  dateKeys.forEach((dateKey, index) => {
    const dayIndexRatio = dateKeys.length > 1 ? index / (dateKeys.length - 1) : 1;
    const cyclePhase = Math.sin((index / 14) * Math.PI * 2);
    const trendDrift = (dayIndexRatio - 0.5) * 2;
    // Ultimas ~2 semanas: acumulacion progresiva de fatiga (viajes, carga alta),
    // para que Trend Engine tenga senales reales que detectar en la demo.
    const recencyStress = clamp((dayIndexRatio - 0.85) / 0.15, 0, 1);

    const tags = pickDailyTags(random);

    // ---- Sueno ----
    let sleepDurationNoise = randomInRange(random, -35, 35);
    let sleepEfficiencyNoise = randomInRange(random, -6, 6);
    let latencyMinutes = randomInRange(random, 5, 20);
    let awakenings = Math.round(randomInRange(random, 0, 3));
    let bedtimeHour = 22.5 + randomInRange(random, -0.5, 1.0) + recencyStress * 1.2;

    if (tags.includes('coffee_late')) {
      latencyMinutes += randomInRange(random, 15, 35);
      sleepEfficiencyNoise -= 5;
    }
    if (tags.includes('alcohol')) {
      sleepEfficiencyNoise -= 8;
      awakenings += 1;
    }
    if (tags.includes('travel')) {
      bedtimeHour += randomInRange(random, 1, 2);
      sleepDurationNoise -= 40;
    }
    if (tags.includes('heavy_dinner')) {
      sleepEfficiencyNoise -= 4;
    }
    if (tags.includes('stress')) {
      latencyMinutes += 10;
      awakenings += 1;
    }

    const totalMinutes = clamp(
      round(profile.sleepDurationBase + cyclePhase * 20 + sleepDurationNoise + trendDrift * 10 - recencyStress * 45),
      280,
      560,
    );
    const efficiency = clamp(round(profile.sleepEfficiencyBase + sleepEfficiencyNoise - recencyStress * 8), 55, 99);
    const deepMinutes = clamp(round(totalMinutes * randomInRange(random, 0.16, 0.22)), 30, 140);
    const remMinutes = clamp(round(totalMinutes * randomInRange(random, 0.18, 0.25)), 30, 150);
    const awakeMinutes = clamp(round(totalMinutes * (1 - efficiency / 100)), 5, 100);
    const coreMinutes = Math.max(totalMinutes - deepMinutes - remMinutes - awakeMinutes, 60);

    const previousDateKey = addDays(dateKey, -1);
    const bedtimeDate = parseDateKey(previousDateKey);
    bedtimeDate.setUTCHours(0, 0, 0, 0);
    const bedtime = new Date(bedtimeDate.getTime() + bedtimeHour * 60 * 60 * 1000);
    const sleepStart = new Date(bedtime.getTime() + latencyMinutes * 60 * 1000);
    const wakeTime = new Date(sleepStart.getTime() + totalMinutes * 60 * 1000);

    // ---- Recuperacion ----
    let hrv = profile.hrvBase + cyclePhase * 4 + trendDrift * 2 - recencyStress * 10 + randomInRange(random, -4, 4);
    let restingHeartRate =
      profile.restingHrBase - cyclePhase * 2 + recencyStress * 6 + randomInRange(random, -3, 3);

    if (previousDayTags.includes('alcohol')) {
      hrv -= randomInRange(random, 4, 9);
      restingHeartRate += randomInRange(random, 3, 6);
    }
    if (previousDayTags.includes('sauna')) {
      hrv += randomInRange(random, 1, 4);
    }
    if (previousDayTags.includes('sick') || tags.includes('sick')) {
      hrv -= randomInRange(random, 5, 10);
      restingHeartRate += randomInRange(random, 4, 9);
    }
    if (previousDayTags.includes('high_workload') || previousDayTags.includes('gym')) {
      restingHeartRate += randomInRange(random, 0, 2);
    }

    hrv = clamp(round(hrv), 20, 95);
    restingHeartRate = clamp(round(restingHeartRate), 40, 88);
    const averageHeartRate = clamp(round(restingHeartRate + randomInRange(random, 15, 35)), 60, 150);
    const minHeartRate = clamp(round(restingHeartRate - randomInRange(random, 3, 8)), 35, restingHeartRate);
    const spo2 = clamp(round(97 + randomInRange(random, -1.5, 1.2), 1), 90, 100);
    const respiratoryRate = clamp(round(14 + randomInRange(random, -1.5, 1.5) + recencyStress * 1, 1), 10, 22);
    const temperatureDeviation = round(
      randomInRange(random, -0.3, 0.3) + (tags.includes('sick') ? randomInRange(random, 0.4, 1.0) : 0),
      2,
    );

    // ---- Actividad ----
    let steps = profile.stepsBase + cyclePhase * 1500 + randomInRange(random, -1800, 1800);
    let exerciseMinutes = Math.max(0, randomInRange(random, 0, 45));
    const workouts: WorkoutSession[] = [];

    if (tags.includes('gym') || tags.includes('cardio')) {
      exerciseMinutes = Math.max(exerciseMinutes, randomInRange(random, 35, 75));
      const workoutStart = new Date(parseDateKey(dateKey).getTime());
      workoutStart.setUTCHours(18, 0, 0, 0);
      const durationMinutes = round(exerciseMinutes);
      const workoutEnd = new Date(workoutStart.getTime() + durationMinutes * 60 * 1000);
      workouts.push({
        id: `${dateKey}-workout`,
        type: tags.includes('gym') ? 'strength_training' : 'running',
        startTime: workoutStart.toISOString(),
        endTime: workoutEnd.toISOString(),
        durationMinutes,
        activeCalories: round(exerciseMinutes * 7),
        averageHeartRate: clamp(round(restingHeartRate + randomInRange(random, 40, 70)), 90, 190),
      });
    }
    if (tags.includes('rest_day')) {
      steps *= 0.55;
      exerciseMinutes = 0;
    }

    steps = clamp(round(steps), 1200, 20000);
    exerciseMinutes = clamp(round(exerciseMinutes), 0, 150);
    const activeCalories = clamp(
      round(profile.activeCaloriesBase * (steps / profile.stepsBase) + exerciseMinutes * 6 + randomInRange(random, -60, 60)),
      100,
      1200,
    );
    const distanceKm = round(steps / 1350, 2);

    // ---- Nutricion ----
    let calories = profile.caloriesBase + trendDrift * 80 + randomInRange(random, -220, 220);
    let protein = profile.proteinBase + randomInRange(random, -25, 25);
    let sugar = randomInRange(random, 20, 70);
    let sodium = randomInRange(random, 1600, 3200);
    const fiber = randomInRange(random, 15, 35);

    if (tags.includes('heavy_dinner')) {
      calories += 350;
      sodium += 400;
      sugar += 15;
    }
    if (tags.includes('alcohol')) {
      calories += 300;
      sugar += 20;
      protein -= 10;
    }

    calories = clamp(round(calories), 1400, 3600);
    protein = clamp(round(protein), 60, 220);
    const carbs = clamp(round((calories * 0.42) / 4 + randomInRange(random, -15, 15)), 80, 420);
    const fat = clamp(round((calories * 0.3) / 9 + randomInRange(random, -6, 6)), 30, 160);
    sugar = clamp(round(sugar), 5, 140);
    sodium = clamp(round(sodium), 900, 4200);

    const nutritionTotals = { calories, protein, carbs, fat, fiber: round(fiber), sodium, sugar };
    const meals = buildDemoMeals(random, dateKey, nutritionTotals);

    // ---- Cuerpo ----
    weightDrift += randomInRange(random, -0.05, 0.05);
    const weightKg = round(profile.weightBase + weightDrift + Math.sin(index / 45) * 0.4, 1);
    const bodyTemperature = round(36.5 + temperatureDeviation, 2);

    history.push({
      date: dateKey,
      source: 'demo',
      sleep: {
        bedtime: bedtime.toISOString(),
        sleepStart: sleepStart.toISOString(),
        wakeTime: wakeTime.toISOString(),
        totalMinutes,
        awakeMinutes,
        coreMinutes,
        deepMinutes,
        remMinutes,
        efficiency,
        latencyMinutes: round(latencyMinutes),
        awakenings: Math.max(awakenings, 0),
      },
      recovery: {
        hrv,
        restingHeartRate,
        averageHeartRate,
        minHeartRate,
        spo2,
        respiratoryRate,
        temperatureDeviation,
      },
      activity: {
        steps,
        activeCalories,
        exerciseMinutes,
        distanceKm,
        workouts,
      },
      nutrition: {
        ...nutritionTotals,
        meals,
      },
      body: {
        weightKg,
        bodyTemperature,
      },
      tags,
    });

    previousDayTags = tags;
  });

  return history;
}
